import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  DataTable,
  Divider,
  IndexTable,
  IndexTableProps,
  InlineGrid,
  InlineStack,
  LegacyCard,
  Link,
  Modal,
  Page,
  Pagination,
  Select,
  SkeletonBodyText,
  Tabs,
  Text,
  TextField,
  useBreakpoints,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import agent from '../../app/api/agent';
import {
  FulfillmentAuditDetail,
  FulfillmentAuditListItem,
  FulfillmentAuditPageResult,
  FulfillmentAuditSummary,
  FulfillmentAuditTraverseCheck,
  FulfillmentAuditTraverseRow,
} from '../../app/models/FulfillmentAudit';

const emptyPage: FulfillmentAuditPageResult = {
  items: [],
  page: 1,
  pageSize: 50,
  totalCount: 0,
  totalPages: 0,
};

const emptySummary: FulfillmentAuditSummary = {
  periodStart: '',
  allActivity: 0,
  needsAttention: 0,
  failureHistory: 0,
  fulfillmentCreates: 0,
  createdWithoutTracking: 0,
  trackingUpdates: 0,
  unresolvedFailures: 0,
  unresolvedTrackingCandidates: 0,
};

const tabDefinitions = [
  { id: 'all', content: 'All activity', accessibilityLabel: 'All fulfillment audit activity' },
  { id: 'attention', content: 'Needs attention' },
  { id: 'failures', content: 'Failure history' },
  { id: 'creates', content: 'Fulfillment creates' },
  { id: 'without-tracking', content: 'Created without tracking' },
  { id: 'tracking-updates', content: 'Tracking updates' },
];

const operationLabels: Record<string, string> = {
  FulfillmentCreate: 'Fulfillment create',
  FulfillmentTrackingUpdate: 'Tracking update',
};

const formatOperation = (operation: string) => operationLabels[operation] || operation;

const conciseErrorMessage = (value: string | null) => {
  if (!value) return 'No error details were recorded.';

  const summaryMatch = value.match(/(?:User Errors|Errors):\s*(?:\r?\n\s*)*-\s*([^\r\n]+)/i);
  const jsonMessageMatch = value.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)"/i);
  let message = summaryMatch?.[1];

  if (!message && jsonMessageMatch?.[1]) {
    try {
      message = JSON.parse(`"${jsonMessageMatch[1]}"`);
    } catch {
      message = jsonMessageMatch[1];
    }
  }

  message ||= value.split(/\r?\n/).find(line => line.trim())?.trim() || value.trim();
  return message.length > 240 ? `${message.slice(0, 237)}...` : message;
};

const formatJson = (value: string | null) => {
  if (!value) return 'No data recorded.';

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const displayOrderName = (audit: FulfillmentAuditListItem) =>
  audit.shopifyOrderName || (audit.shopifyOrderId ? String(audit.shopifyOrderId) : 'Unknown order');

const prepareTraverseTableRows = (rows: FulfillmentAuditTraverseRow[]) => rows.map(row => [
  row.transId || '',
  row.lineSku || '',
  row.lineQuantity == null ? '' : String(row.lineQuantity),
  row.trackingCompany?.trim() || '',
  row.trackingNumber || '',
  row.shipDate ? dayjs(row.shipDate).format('MMM D, YYYY') : '',
]);

const statusBadge = (audit: FulfillmentAuditListItem) => {
  if (audit.isResolved) {
    return <Badge tone="success">Resolved</Badge>;
  }

  if (audit.status === 'Success' && audit.requiresAttention) {
    return <Badge tone="info">Tracking queued</Badge>;
  }

  if (audit.status === 'Success') {
    return <Badge tone="success">Success</Badge>;
  }

  return <Badge tone="critical">{audit.status}</Badge>;
};

const traverseCheckTone = (check: FulfillmentAuditTraverseCheck) => {
  if (check.status === 'Match') return 'success' as const;
  if (check.status === 'PendingRefresh' || check.status === 'CacheRefreshInProgress' || check.status === 'NoData') {
    return 'info' as const;
  }
  if (check.status === 'Mismatch') return 'warning' as const;
  return 'critical' as const;
};

const traverseCheckTitle = (check: FulfillmentAuditTraverseCheck) => {
  switch (check.status) {
    case 'Match':
      return 'Live source matches the sync cache';
    case 'PendingRefresh':
      return 'Pending hourly cache refresh';
    case 'CacheRefreshInProgress':
      return 'Cache refresh in progress';
    case 'CacheRefreshFailed':
      return 'Latest cache refresh failed';
    case 'NoData':
      return 'No shipment data found';
    case 'Mismatch':
      return 'Difference remains outside the refresh window';
    default:
      return 'Live Traverse is unavailable';
  }
};

const FulfillmentAuditPage = () => {
  const { smDown } = useBreakpoints();
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('14');
  const [page, setPage] = useState(1);
  const [pageResult, setPageResult] = useState<FulfillmentAuditPageResult>(emptyPage);
  const [summary, setSummary] = useState<FulfillmentAuditSummary>(emptySummary);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<FulfillmentAuditDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [traverseCheck, setTraverseCheck] = useState<FulfillmentAuditTraverseCheck | null>(null);
  const [traverseCheckLoading, setTraverseCheckLoading] = useState(false);
  const [traverseCheckError, setTraverseCheckError] = useState<string | null>(null);
  const tabs = useMemo(() => [
    { ...tabDefinitions[0], content: `All activity (${summary.allActivity.toLocaleString()})` },
    { ...tabDefinitions[1], content: `Needs attention (${summary.needsAttention.toLocaleString()})` },
    { ...tabDefinitions[2], content: `Failure history (${summary.failureHistory.toLocaleString()})` },
    { ...tabDefinitions[3], content: `Fulfillment creates (${summary.fulfillmentCreates.toLocaleString()})` },
    { ...tabDefinitions[4], content: `Created without tracking (${summary.createdWithoutTracking.toLocaleString()})` },
    { ...tabDefinitions[5], content: `Tracking updates (${summary.trackingUpdates.toLocaleString()})` },
  ], [summary]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
      setPage(1);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  const loadActivity = useCallback(async () => {
    setError(null);
    try {
      const selectedView = tabDefinitions[selectedTab].id;
      const result = await agent.FulfillmentAudit.list({
        search: debouncedSearch || undefined,
        view: selectedView === 'all' ? undefined : selectedView,
        status: status || undefined,
        days: Number(days),
        page,
        pageSize: 50,
      });
      setPageResult(result);
    } catch {
      setError('Fulfillment audit activity could not be loaded.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [days, debouncedSearch, page, selectedTab, status]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await agent.FulfillmentAudit.summary({
        search: debouncedSearch || undefined,
        status: status || undefined,
        days: Number(days),
      }));
    } catch {
      setError('Fulfillment audit summary could not be loaded.');
    }
  }, [days, debouncedSearch, status]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    Promise.all([loadActivity(), loadSummary()]).finally(() => setIsRefreshing(false));
  }, [loadActivity, loadSummary]);

  const openDetails = useCallback(async (id: number) => {
    setDetailLoading(true);
    setError(null);
    setTraverseCheck(null);
    setTraverseCheckError(null);
    try {
      setSelectedDetail(await agent.FulfillmentAudit.details(id));
    } catch {
      setError('Fulfillment audit details could not be loaded.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const checkLiveTraverse = useCallback(async () => {
    if (!selectedDetail) return;

    setTraverseCheckLoading(true);
    setTraverseCheckError(null);
    try {
      setTraverseCheck(await agent.FulfillmentAudit.checkTraverseSource(selectedDetail.audit.id));
    } catch {
      setTraverseCheckError('The live Traverse verification request failed.');
    } finally {
      setTraverseCheckLoading(false);
    }
  }, [selectedDetail]);

  const closeDetails = useCallback(() => {
    setSelectedDetail(null);
    setTraverseCheck(null);
    setTraverseCheckError(null);
  }, []);

  const headings = useMemo(() => [
    { title: 'Attempted at' },
    { title: 'Operation' },
    { title: 'Shopify order' },
    { title: 'Tracking' },
    { title: 'Status' },
    { title: '' },
  ], []);

  const rows = pageResult.items.map((audit, index) => (
    <IndexTable.Row
      id={String(audit.id)}
      key={audit.id}
      position={index}
      selected={false}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodySm">
          {dayjs(audit.attemptedAt).format('MMM D, YYYY h:mm A')}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatOperation(audit.operation)}</IndexTable.Cell>
      <IndexTable.Cell>
        <BlockStack gap="050">
          <Text as="span" fontWeight="semibold">{displayOrderName(audit)}</Text>
          <Text as="span" variant="bodySm" tone="subdued">
            FO {audit.fulfillmentOrderId}
          </Text>
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {audit.trackingNumber ? (
          <BlockStack gap="050">
            <Text as="span">{audit.trackingNumber}</Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {audit.trackingCompany?.trim() || 'Carrier not supplied'}
            </Text>
          </BlockStack>
        ) : (
          <Text as="span" tone="subdued">Not supplied</Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>{statusBadge(audit)}</IndexTable.Cell>
      <IndexTable.Cell>
        <Button variant="plain" onClick={() => openDetails(audit.id)}>
          View
        </Button>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const cachedTraverseRows = prepareTraverseTableRows(
    traverseCheck?.cachedRows || selectedDetail?.traverseRows || []
  );
  const liveTraverseRows = prepareTraverseTableRows(traverseCheck?.sourceRows || []);

  return (
    <Page
      title="Fulfillments"
      primaryAction={{ content: 'Refresh', onAction: refresh, loading: isRefreshing }}
    >
      <BlockStack gap="500">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}

        {summary.unresolvedFailures > 0 && (
          <Banner tone="warning" title="Fulfillment activity needs review">
            <p>
              {summary.unresolvedFailures} unresolved failure(s) in the selected timeframe require review.
              {summary.unresolvedTrackingCandidates > 0 && (
                <> {summary.unresolvedTrackingCandidates} tracking update(s) are also queued for automatic processing.</>
              )}
            </p>
          </Banner>
        )}

        {summary.unresolvedFailures === 0 && summary.unresolvedTrackingCandidates > 0 && (
          <Banner tone="info" title="Tracking updates queued">
            <p>
              {summary.unresolvedTrackingCandidates} fulfillment(s) now have tracking available and are waiting for the next automatic update cycle.
            </p>
          </Banner>
        )}

        <BlockStack gap="300">
          <div className="fulfillment-audit__filters">
            <TextField
              label="Search"
              labelHidden
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Order, fulfillment, tracking number, SKU, or error"
              prefix={<SearchIcon />}
              clearButton
              onClearButtonClick={() => setSearchValue('')}
              autoComplete="off"
            />
            <Select
              label="Status"
              labelHidden
              value={status}
              onChange={(value) => { setStatus(value); setPage(1); }}
              options={[
                { label: 'All statuses', value: '' },
                { label: 'Success', value: 'Success' },
                { label: 'Error', value: 'Error' },
                { label: 'Exception', value: 'Exception' },
              ]}
            />
            <Select
              label="Date range"
              labelHidden
              value={days}
              onChange={(value) => { setDays(value); setPage(1); }}
              options={[
                { label: 'Last 24 hours', value: '1' },
                { label: 'Last 7 days', value: '7' },
                { label: 'Last 14 days', value: '14' },
                { label: 'Last 30 days', value: '30' },
                { label: 'Last 90 days', value: '90' },
                { label: 'All history', value: '0' },
              ]}
            />
          </div>

          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onSelect={(index) => {
              setSelectedTab(index);
              setPage(1);
            }}
          />

          <LegacyCard>
          {isLoading ? (
            <LegacyCard.Section>
              <SkeletonBodyText lines={8} />
            </LegacyCard.Section>
          ) : pageResult.items.length === 0 ? (
            <LegacyCard.Section>
              <Box paddingBlock="800">
                <BlockStack gap="200" inlineAlign="center">
                  <Text as="h2" variant="headingMd">No audit activity found</Text>
                  <Text as="p" tone="subdued">Adjust the search, filters, or date range.</Text>
                </BlockStack>
              </Box>
            </LegacyCard.Section>
          ) : (
            <IndexTable
              condensed={smDown}
              resourceName={{ singular: 'audit event', plural: 'audit events' }}
              itemCount={pageResult.items.length}
              headings={headings as IndexTableProps['headings']}
              selectable={false}
            >
              {rows}
            </IndexTable>
          )}

          {pageResult.totalCount > 0 && (
            <LegacyCard.Section>
              <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                <Text as="p" tone="subdued">
                  {pageResult.totalCount.toLocaleString()} event(s), page {pageResult.page} of {pageResult.totalPages}
                </Text>
                <Pagination
                  hasPrevious={pageResult.page > 1}
                  onPrevious={() => setPage(current => Math.max(1, current - 1))}
                  hasNext={pageResult.page < pageResult.totalPages}
                  onNext={() => setPage(current => current + 1)}
                />
              </InlineStack>
            </LegacyCard.Section>
          )}
          </LegacyCard>
        </BlockStack>
      </BlockStack>

      <Modal
        open={selectedDetail !== null || detailLoading}
        onClose={closeDetails}
        title={selectedDetail ? `${displayOrderName(selectedDetail.audit)} fulfillment details` : 'Loading fulfillment details'}
        size="large"
      >
        {detailLoading || !selectedDetail ? (
          <Modal.Section><SkeletonBodyText lines={8} /></Modal.Section>
        ) : (
          <>
            <Modal.Section>
              <BlockStack gap="400">
                <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                  <BlockStack gap="050">
                    <Text as="p" variant="bodySm" tone="subdued">Operation</Text>
                    <Text as="p" fontWeight="semibold">{formatOperation(selectedDetail.audit.operation)}</Text>
                  </BlockStack>
                  <BlockStack gap="050">
                    <Text as="p" variant="bodySm" tone="subdued">Status</Text>
                    <div>{statusBadge(selectedDetail.audit)}</div>
                  </BlockStack>
                  <BlockStack gap="050">
                    <Text as="p" variant="bodySm" tone="subdued">Fulfillment order</Text>
                    <Text as="p" fontWeight="semibold">{selectedDetail.audit.fulfillmentOrderId}</Text>
                  </BlockStack>
                  <BlockStack gap="050">
                    <Text as="p" variant="bodySm" tone="subdued">Shopify fulfillment</Text>
                    <Text as="p" fontWeight="semibold">{selectedDetail.audit.fulfillmentId || 'Not returned'}</Text>
                  </BlockStack>
                </InlineGrid>

                {selectedDetail.audit.errorMessage && (
                  <Banner
                    tone={selectedDetail.audit.isResolved ? 'success' : 'critical'}
                    title={selectedDetail.audit.isResolved ? 'Resolved historical error' : 'Recorded error'}
                  >
                    <p>{conciseErrorMessage(selectedDetail.audit.errorMessage)}</p>
                  </Banner>
                )}

                <Divider />
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Tracking sent to Shopify</Text>
                  <InlineStack gap="400" wrap>
                    <Text as="p">{selectedDetail.audit.trackingCompany?.trim() || 'Carrier not supplied'}</Text>
                    <Text as="p">{selectedDetail.audit.trackingNumber || 'Tracking number not supplied'}</Text>
                    {selectedDetail.audit.trackingUrl && (
                      <Link url={selectedDetail.audit.trackingUrl} target="_blank">Open tracking URL</Link>
                    )}
                  </InlineStack>
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Line items recorded</Text>
                  <pre className="fulfillment-audit__payload">{formatJson(selectedDetail.audit.lineItems)}</pre>
                </BlockStack>
              </BlockStack>
            </Modal.Section>

            <Modal.Section>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">Audit timeline</Text>
                {selectedDetail.timeline.map(item => (
                  <div className="fulfillment-audit__timeline-row" key={item.id}>
                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                      <InlineStack gap="200" blockAlign="center" wrap>
                        {statusBadge(item)}
                        <Text as="span" fontWeight="semibold">{formatOperation(item.operation)}</Text>
                        {item.trackingNumber && <Text as="span">{item.trackingNumber}</Text>}
                      </InlineStack>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {dayjs(item.attemptedAt).format('MMM D, YYYY h:mm:ss A')}
                      </Text>
                    </InlineStack>
                    {item.status !== 'Success' && (
                      <div className="fulfillment-audit__timeline-message">
                        <Text as="p" variant="bodySm" tone={item.isResolved ? 'subdued' : 'critical'}>
                          {conciseErrorMessage(item.errorMessage)}
                        </Text>
                        {item.isResolved && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            Resolved by a later successful attempt.
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </BlockStack>
            </Modal.Section>

            <Modal.Section>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                  <Text as="h3" variant="headingMd">Traverse source verification</Text>
                  <Button
                    icon={SearchIcon}
                    onClick={checkLiveTraverse}
                    loading={traverseCheckLoading}
                  >
                    Check live Traverse
                  </Button>
                </InlineStack>

                {traverseCheckError && (
                  <Banner tone="critical"><p>{traverseCheckError}</p></Banner>
                )}

                {traverseCheck && (
                  <Banner
                    tone={traverseCheckTone(traverseCheck)}
                    title={traverseCheckTitle(traverseCheck)}
                  >
                    <BlockStack gap="100">
                      <p>{traverseCheck.message}</p>
                      {traverseCheck.cachePulledAt && (
                        <p>
                          Cache last refreshed {dayjs(traverseCheck.cachePulledAt).format('MMM D, YYYY h:mm:ss A')}.
                        </p>
                      )}
                      {traverseCheck.nextCachePullExpectedAt && (
                        <p>
                          Next hourly refresh expected around {dayjs(traverseCheck.nextCachePullExpectedAt).format('MMM D, YYYY h:mm A')}.
                        </p>
                      )}
                      {traverseCheck.cachePullError && <p>{traverseCheck.cachePullError}</p>}
                      {traverseCheck.errorMessage && <p>{traverseCheck.errorMessage}</p>}
                      {traverseCheck.differences.map((difference, index) => (
                        <p key={index}>{difference}</p>
                      ))}
                      <Text as="p" variant="bodySm" tone="subdued">
                        Checked {dayjs(traverseCheck.checkedAt).format('MMM D, YYYY h:mm:ss A')}
                      </Text>
                    </BlockStack>
                  </Banner>
                )}

                {traverseCheck && (
                  <BlockStack gap="200">
                    <Text as="h4" variant="headingSm">Live Traverse source</Text>
                    {liveTraverseRows.length ? (
                      <DataTable
                        columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text']}
                        headings={['Trans ID', 'SKU', 'Quantity', 'Carrier', 'Tracking number', 'Ship date']}
                        rows={liveTraverseRows}
                      />
                    ) : (
                      <Text as="p" tone="subdued">No matching rows returned by the live Traverse view.</Text>
                    )}
                  </BlockStack>
                )}

                <Divider />
                <Text as="h4" variant="headingSm">Sync cache (hourly)</Text>
                {cachedTraverseRows.length ? (
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text']}
                    headings={['Trans ID', 'SKU', 'Quantity', 'Carrier', 'Tracking number', 'Ship date']}
                    rows={cachedTraverseRows}
                  />
                ) : (
                  <Text as="p" tone="subdued">No matching rows are currently available in the sync cache.</Text>
                )}
              </BlockStack>
            </Modal.Section>

            <Modal.Section>
              <BlockStack gap="400">
                <details className="fulfillment-audit__payload-disclosure">
                  <summary>Request payload</summary>
                  <pre className="fulfillment-audit__payload">{formatJson(selectedDetail.requestJson)}</pre>
                </details>
                <details className="fulfillment-audit__payload-disclosure">
                  <summary>Shopify response</summary>
                  <pre className="fulfillment-audit__payload">{formatJson(selectedDetail.responseJson)}</pre>
                </details>
              </BlockStack>
            </Modal.Section>
          </>
        )}
      </Modal>
    </Page>
  );
};

export default FulfillmentAuditPage;
